import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidePanel from "../ui/SidePanel";
import { LoadSummary } from "../ui/cards";
import { VehiclePhoto } from "../search/icons";
import { errText } from "../search/quick";
import { fetchTrucks, proposeTrucks } from "../../utils/matchingApi";
import { API, authHeaders, CURRENCY_SYMBOL } from "./common";
import { fmtFree, haversine, nf } from "../search/liveTrucks";

/**
 * «Предложить транспорт»: the load's summary in a side panel plus «Выберите ваш транспорт» — the carrier ticks one or
 * several of their free trucks, sets the price (the stated rate by default) and sends the offer
 * (POST /m/loads/:id/propose; trucks the engine did not pair are paired by hand).
 */
export default function RespondPanel({ order: o, user, carrier, matchedVehicleIds = [], onClose, onDone }) {
  const sign = CURRENCY_SYMBOL[o.currency] || "сом";
  const [trucks, setTrucks] = useState(null);
  const [picked, setPicked] = useState([]);
  const [rate, setRate] = useState(o.price != null ? String(o.price) : "");
  const [phone, setPhone] = useState(user?.phone_number || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentModels, setSentModels] = useState(null);

  useEffect(() => {
    if (!carrier) {
      setTrucks([]);
      return undefined;
    }
    let alive = true;
    fetchTrucks()
      .then((d) => {
        if (!alive) return;
        const mine = (d?.trucks || []).filter((t) => t.driver_id === user?.id && t.status === "ACTIVE");
        setTrucks(mine);
        // trucks the engine already paired with this load start ticked
        setPicked(mine.filter((t) => matchedVehicleIds.includes(Number(t.vehicle_id))).map((t) => Number(t.vehicle_id)));
      })
      .catch(() => alive && setTrucks([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrier, user?.id]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const pickedSet = useMemo(() => new Set(picked), [picked]);

  const submit = async () => {
    setError("");
    if (!carrier) return setError("Откликаться на грузы могут водитель или компания-перевозчик.");
    if (!picked.length) return setError("Отметьте хотя бы одну машину.");
    if (!(Number(rate) > 0)) return setError("Укажите ставку.");
    setBusy(true);
    try {
      if (phone.trim() && phone.trim() !== (user?.phone_number || "")) {
        await axios.patch(`${API}/auth/users/me/`, { phone_number: phone.trim() }, { headers: authHeaders() }).catch(() => null);
      }
      const same = o.price != null && Number(rate) === Number(o.price);
      const r = await proposeTrucks(o.uuid, {
        vehicle_ids: picked,
        ...(same ? {} : { amount: Number(rate), ...(o.currency ? { currency: Number(o.currency) } : {}) }),
      });
      onDone(r?.offers?.length || 0, r?.failed?.length || 0);
      setSentModels(
        trucks
          .filter((t) => picked.includes(Number(t.vehicle_id)))
          .map((t) => t.mark || t.government_number || "машина")
          .join(", ")
      );
      setBusy(false);
    } catch (e) {
      setError(errText(e, "Не удалось отправить отклик"));
      setBusy(false);
    }
  };

  const navigate = useNavigate();
  const from = o.location_from?.lat != null && o.location_from?.lng != null ? { lat: Number(o.location_from.lat), lng: Number(o.location_from.lng) } : null;
  const kmFromLoad = (t) => (from && t.lat != null && t.lng != null ? Math.round(haversine(from, { lat: Number(t.lat), lng: Number(t.lng) })) : null);

  if (sentModels !== null) {
    return (
      <Confirmation
        models={sentModels}
        onClose={onClose}
        onGo={() => {
          onClose();
          navigate("/notifications");
        }}
      />
    );
  }

  return (
    <SidePanel
      className="details"
      title="Предложить транспорт"
      subtitle={o.uuid ? `Груз #${String(o.uuid).toUpperCase()}` : ""}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="button primary" disabled={busy || !carrier || !picked.length} onClick={submit} type="button">
            {busy ? "Отправляем…" : picked.length > 1 ? `Отправить предложение (${picked.length})` : "Отправить предложение"}
          </button>
          <button className="button" onClick={onClose} type="button">
            Отмена
          </button>
        </>
      }
    >
      <LoadSummary order={o} />
      <div className="cf-form">
        <section>
          <h3>Выберите ваш транспорт</h3>
          <p className="cf-muted">Отметьте автомобиль (или несколько) и отправьте предложение</p>
          {trucks === null ? <p className="side-note">Загружаем ваши машины…</p> : null}
          {trucks && trucks.length === 0 ? (
            <p className="side-note">
              {carrier
                ? "У вас нет свободных машин. Добавьте свободную машину в разделе «Мой транспорт» и вернитесь к отклику."
                : "Откликаться на грузы могут водитель или компания-перевозчик."}
            </p>
          ) : null}
          {trucks && trucks.length ? (
            <div className="cf-vehicles">
              {trucks.map((t) => {
                const id = Number(t.vehicle_id);
                const on = pickedSet.has(id);
                const km = kmFromLoad(t);
                const free = fmtFree(t);
                const spec = [t.body_type_name, t.max_load_weight != null ? `${nf(t.max_load_weight)} т` : null, t.max_load_volume != null ? `${nf(t.max_load_volume)} м³` : null].filter(Boolean).join(" • ");
                return (
                  <label className={`cf-vehicle${on ? " on" : ""}`} key={id}>
                    <input checked={on} onChange={() => toggle(id)} type="checkbox" />
                    <VehiclePhoto label={t.mark || "Машина"} />
                    <span className="cf-main">
                      <b>{t.mark || "Машина"}</b>
                      {spec ? <small>{spec}</small> : null}
                      {t.government_number ? <small>{t.government_number}</small> : null}
                      {matchedVehicleIds.includes(id) ? <small>подходит под груз</small> : null}
                    </span>
                    <span className="cf-location">
                      {t.city || "—"}
                      {km != null ? <small>{nf(km)} км от груза</small> : null}
                    </span>
                    <span className="cf-ready">
                      Свободен
                      <small>{free === "Сегодня" || free === "Завтра" ? free.toLowerCase() : `с ${free}`}</small>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </section>
        {picked.length ? (
          <section>
            <h3>Ваше предложение</h3>
            <div className="cf-fields">
              <label>
                Ваша ставка, {sign}
                <input min="0" onChange={(e) => setRate(e.target.value)} step="any" type="number" value={rate} />
              </label>
              <label>
                Ваш телефон
                <input onChange={(e) => setPhone(e.target.value)} type="tel" value={phone} />
              </label>
            </div>
          </section>
        ) : null}
        <p className="cf-notice">После отправки предложения грузовладелец получит ваши контакты и сможет связаться с вами напрямую.</p>
        {error ? (
          <p className="side-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </SidePanel>
  );
}

/** «Предложение отправлено» */
function Confirmation({ models, onClose, onGo }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.querySelector(".button")?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="confirmation-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label="Предложение отправлено">
      <div className="confirmation-card" ref={ref}>
        <button aria-label="Закрыть" className="confirm-x" onClick={onClose} type="button">
          ×
        </button>
        <span className="success-check">✓</span>
        <h2>Предложение отправлено</h2>
        <p>Ваше предложение с автомобилем {models} успешно отправлено грузовладельцу.</p>
        <small>Вы получите уведомление, когда грузовладелец ответит на ваше предложение.</small>
        <div className="actions">
          <button className="button primary" onClick={onGo} type="button">
            Перейти к предложению
          </button>
          <button className="button" onClick={onClose} type="button">
            Остаться
          </button>
        </div>
      </div>
    </div>
  );
}
